import { Ban, BadgeCheck, CheckCircle2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { EligibilityModal } from '../components/EligibilityModal';
import { eligibilitiesApi } from '../eligibilitiesApi';
import type { EligibilityListItem } from '../eligibilityTypes';

type TabKey = 'info';

/**
 * One eligibility in the catalog.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — a name, what the forms do with it, and the trail of who changed it — not a
 * record whose content splits into panels a reader chooses between.
 *
 * **It exists because "there is nothing behind this row but its own two fields" was never an
 * exemption.** The list's anchor opened the edit modal until this page, and that argument was
 * an argument for a THIN profile rather than for no profile: the count of titles requiring an
 * eligibility, whether it entails a licence, and who last touched the word are all things a
 * reader wants before renaming it or deleting it — and none of them belong in a form.
 */
export function EligibilityProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [eligibility, setEligibility] = useState<EligibilityListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      eligibilitiesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setEligibility(next);

          // Cleared on success, or a failed restore would keep its banner over a page that has
          // since re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this eligibility.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Puts a deleted eligibility back.
   *
   * **No confirm tick and no password.** Restoring puts something back, and ceremony on an undo
   * only teaches readers to click through ceremony. The server still refuses it when another
   * active row has taken the name meanwhile, and that refusal arrives in the banner.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await eligibilitiesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not restore this eligibility.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: 'Eligibilities', to: '/personnel/settings/eligibilities' },
    eligibility?.name ?? 'Eligibility',
  ];

  /*
    Guarded on having NOTHING to show, never on `error` alone. The full-page state is for a
    first load that never arrived; once a record is up it is still the last good answer, and
    blanking it because a restore was refused would throw away what the reader is looking at in
    order to report a failure about one button. That failure goes to the banner below.
  */
  if (error && !eligibility) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !eligibility) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { positionCount } = eligibility;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="eligibility-profile"
        header={
          <ProfileHeader
            id="eligibility-profile-header"
            icon={<BadgeCheck className="h-6 w-6" aria-hidden="true" />}
            title={eligibility.name}
            meta={[
              /*
                Spelled out rather than left as a bare "3 positions", which the family-relation
                header gets away with and this one cannot: a count beside an eligibility could
                as easily be the employees who hold it, and those are different questions asked
                from the same page.
              */
              `${positionCount} ${positionCount === 1 ? 'position requires' : 'positions require'} it`,
              ...(eligibility.entailsLicense ? ['Entails a licence'] : []),
            ]}
            status={
              <StatusText tone={eligibility.isActive ? 'active' : 'inactive'}>
                {eligibility.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={eligibility.createdByName}
            createdAt={eligibility.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="eligibility-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : eligibility.isActive ? (
            <ProfileBanner
              id="eligibility-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This eligibility is offered: it appears in the Eligibility picker on a position's qualification standards and on every personnel record's eligibility form."
            />
          ) : (
            <ProfileBanner
              id="eligibility-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This eligibility is deleted. It is no longer offered, and the positions and personnel records already naming it keep resolving its name."
              action={
                // Gates the button, not the banner: a read-only viewer still needs to see the
                // state. `delete` is the key, because restoring is that action's own reversal.
                <PermissionGate permission="eligibilities.delete">
                  <Button
                    id="eligibility-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="eligibility-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'eligibilities.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: eligibility.name },
              { label: 'Description', value: eligibility.description },

              /*
                The licence flag sits on THIS card rather than one of its own, for the reason
                the family relation's three flags do: a reader came to correct what the row
                does, and which half of that is a name and which is a checkbox is our storage
                layout, not their question. One card is one modal, and this card has the Edit.
              */
              {
                label: 'Licence',
                value: eligibility.entailsLicense
                  ? 'Entailed — the personnel form asks for a licence number and its date of validity, and requires them'
                  : 'Not entailed — neither field is drawn',
              },
              {
                label: 'Positions requiring it',
                /*
                  A zero is MUTED, like every other quantity in the product: an eligibility no
                  title requires is the ordinary case — a new one, or one only 201 files name —
                  and the count worth reading is the one that is not zero. Muted, never an
                  em-dash: "no position requires this" and "we do not know" are different facts.
                */
                value: (
                  <span
                    className={`font-mono tabular-nums ${positionCount === 0 ? 'text-slate-300' : ''}`}
                  >
                    {positionCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="eligibility-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: eligibility.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(eligibility.createdAt)} className="font-mono">
                    {formatDateTime(eligibility.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: eligibility.updatedByName },
              {
                label: 'Updated',
                value: eligibility.updatedAt ? (
                  <time dateTime={toIso(eligibility.updatedAt)} className="font-mono">
                    {formatDateTime(eligibility.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <EligibilityModal
          open
          editing={eligibility}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
